import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const LOGO_URL = "https://img1.wsimg.com/isteam/ip/d9f026ae-fdac-4ce0-8e76-8f2f65f8f36d/CafeSales%20Logo.png/:/rs=h:139,cg:true,m/qt=q:95";

const stateDropdownItems = [
    { name: "New South Wales", path: "/recent-sales/nsw", shortName: "NSW" },
    { name: "Queensland", path: "/recent-sales/qld", shortName: "QLD" },
    { name: "Victoria", path: "/recent-sales/vic", shortName: "VIC" },
    { name: "Western Australia", path: "/recent-sales/wa", shortName: "WA" },
];

const Navigation = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isRecentSalesOpen, setIsRecentSalesOpen] = useState(false);
    const [isMobileRecentSalesOpen, setIsMobileRecentSalesOpen] = useState(false);
    const dropdownRef = useRef(null);
    const location = useLocation();

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsRecentSalesOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const navLinks = [
        { name: "Home", path: "/" },
        { name: "Why Cafe Sales Experts?", path: "/#why-us" },
        { name: "Cafes For Sale", path: "https://ads.bonzabfs.com.au/search", external: true },
        { name: "Recent Sales", path: "/#recent-sales", hasDropdown: true },
        { name: "Cafe Valuer", path: "/cafe-valuer" },
        { name: "Articles", path: "/articles" },
    ];

    const isActive = (path) => {
        if (path.startsWith("/#")) return false;
        if (path.startsWith("/recent-sales")) return location.pathname.startsWith("/recent-sales");
        return location.pathname === path;
    };

    const handleNavClick = (e, link) => {
        if (link.hasDropdown) {
            e.preventDefault();
            setIsRecentSalesOpen(!isRecentSalesOpen);
            return;
        }
        if (link.path.startsWith("/#")) {
            e.preventDefault();
            const sectionId = link.path.replace("/#", "");
            
            if (location.pathname !== "/") {
                window.location.href = link.path;
            } else {
                const element = document.getElementById(sectionId);
                if (element) {
                    element.scrollIntoView({ behavior: "smooth" });
                }
            }
        }
        setIsMenuOpen(false);
        setIsRecentSalesOpen(false);
    };

    return (
        <header className="sticky top-0 z-50 bg-white shadow-sm">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-20">
                    {/* Logo */}
                    <Link to="/" className="flex-shrink-0">
                        <img
                            src={LOGO_URL}
                            alt="Cafe Sales Experts"
                            className="h-12 md:h-16 object-contain"
                            data-testid="site-logo"
                        />
                    </Link>

                    {/* Desktop Navigation */}
                    <nav className="hidden lg:flex items-center gap-1">
                        {navLinks.map((link) => (
                            link.external ? (
                                <a
                                    key={link.name}
                                    href={link.path}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-primary transition-colors rounded-lg hover:bg-gray-50"
                                >
                                    {link.name}
                                </a>
                            ) : link.hasDropdown ? (
                                <div key={link.name} className="relative" ref={dropdownRef}>
                                    <button
                                        onClick={(e) => handleNavClick(e, link)}
                                        className={`flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors rounded-lg ${
                                            isActive("/recent-sales")
                                                ? "text-primary bg-primary/10"
                                                : "text-gray-700 hover:text-primary hover:bg-gray-50"
                                        }`}
                                        data-testid="recent-sales-dropdown"
                                    >
                                        {link.name}
                                        <ChevronDown className={`w-4 h-4 transition-transform ${isRecentSalesOpen ? 'rotate-180' : ''}`} />
                                    </button>
                                    
                                    <AnimatePresence>
                                        {isRecentSalesOpen && (
                                            <motion.div
                                                initial={{ opacity: 0, y: -10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -10 }}
                                                transition={{ duration: 0.2 }}
                                                className="absolute top-full left-0 mt-2 w-56 bg-white rounded-xl shadow-lg border py-2 z-50"
                                            >
                                                {stateDropdownItems.map((state) => (
                                                    <Link
                                                        key={state.shortName}
                                                        to={state.path}
                                                        onClick={() => setIsRecentSalesOpen(false)}
                                                        className={`block px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors ${
                                                            location.pathname === state.path
                                                                ? "text-primary bg-primary/5"
                                                                : "text-gray-700"
                                                        }`}
                                                    >
                                                        <span className="font-medium">{state.name}</span>
                                                        <span className="text-gray-400 ml-2">({state.shortName})</span>
                                                    </Link>
                                                ))}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            ) : (
                                <Link
                                    key={link.name}
                                    to={link.path.startsWith("/#") ? "/" : link.path}
                                    onClick={(e) => handleNavClick(e, link)}
                                    className={`px-4 py-2 text-sm font-medium transition-colors rounded-lg ${
                                        isActive(link.path)
                                            ? "text-primary bg-primary/10"
                                            : "text-gray-700 hover:text-primary hover:bg-gray-50"
                                    }`}
                                >
                                    {link.name}
                                </Link>
                            )
                        ))}
                    </nav>

                    {/* Phone CTA */}
                    <div className="hidden md:flex items-center gap-4">
                        <a
                            href="tel:1800370240"
                            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors"
                            data-testid="call-now-btn"
                        >
                            <Phone className="w-4 h-4" />
                            <span>1800 370240</span>
                        </a>
                    </div>

                    {/* Mobile Menu Button */}
                    <button
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
                        data-testid="mobile-menu-btn"
                    >
                        {isMenuOpen ? (
                            <X className="w-6 h-6 text-gray-700" />
                        ) : (
                            <Menu className="w-6 h-6 text-gray-700" />
                        )}
                    </button>
                </div>
            </div>

            {/* Mobile Menu */}
            <AnimatePresence>
                {isMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="lg:hidden bg-white border-t"
                    >
                        <nav className="px-4 py-4 space-y-2">
                            {navLinks.map((link) => (
                                link.external ? (
                                    <a
                                        key={link.name}
                                        href={link.path}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={() => setIsMenuOpen(false)}
                                        className="block px-4 py-3 text-gray-700 hover:text-primary hover:bg-gray-50 rounded-lg font-medium transition-colors"
                                    >
                                        {link.name}
                                    </a>
                                ) : link.hasDropdown ? (
                                    <div key={link.name}>
                                        <button
                                            onClick={() => setIsMobileRecentSalesOpen(!isMobileRecentSalesOpen)}
                                            className="w-full flex items-center justify-between px-4 py-3 text-gray-700 hover:text-primary hover:bg-gray-50 rounded-lg font-medium transition-colors"
                                        >
                                            {link.name}
                                            <ChevronDown className={`w-4 h-4 transition-transform ${isMobileRecentSalesOpen ? 'rotate-180' : ''}`} />
                                        </button>
                                        <AnimatePresence>
                                            {isMobileRecentSalesOpen && (
                                                <motion.div
                                                    initial={{ opacity: 0, height: 0 }}
                                                    animate={{ opacity: 1, height: "auto" }}
                                                    exit={{ opacity: 0, height: 0 }}
                                                    className="ml-4 mt-1 space-y-1 overflow-hidden"
                                                >
                                                    {stateDropdownItems.map((state) => (
                                                        <Link
                                                            key={state.shortName}
                                                            to={state.path}
                                                            onClick={() => {
                                                                setIsMenuOpen(false);
                                                                setIsMobileRecentSalesOpen(false);
                                                            }}
                                                            className="block px-4 py-2 text-gray-600 hover:text-primary hover:bg-gray-50 rounded-lg transition-colors"
                                                        >
                                                            {state.name} ({state.shortName})
                                                        </Link>
                                                    ))}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                ) : (
                                    <Link
                                        key={link.name}
                                        to={link.path.startsWith("/#") ? "/" : link.path}
                                        onClick={(e) => handleNavClick(e, link)}
                                        className={`block px-4 py-3 rounded-lg font-medium transition-colors ${
                                            isActive(link.path)
                                                ? "text-primary bg-primary/10"
                                                : "text-gray-700 hover:text-primary hover:bg-gray-50"
                                        }`}
                                    >
                                        {link.name}
                                    </Link>
                                )
                            ))}
                            <a
                                href="tel:1800370240"
                                className="flex items-center justify-center gap-2 mt-4 px-4 py-3 bg-primary text-white rounded-lg font-medium"
                            >
                                <Phone className="w-4 h-4" />
                                <span>Call 1800 370240</span>
                            </a>
                        </nav>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
};

export default Navigation;
